import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Clipboard, Eraser, Undo, Loader2, FileImage, FileQuestion, FileText } from "lucide-react";
import styles from './translate.module.css';
import { TranscribeButton } from "./transcribe";
import { ImageToTextButton } from "./image-to-text";
import { SelectedLanguage } from "./lang-dropdown";
import { TranslationSettings } from "./translation-settings";
import { CharacterCounter } from "./character-counter";
import { StarkySenseToggleButton } from './starky-sense-toggle';
import { TranslationContext } from "@/app/[locale]/api/translate/route";
import { hasAnyValue, pasteFromClipboard } from "@/lib/utils";
import { processImageFile } from "@/utils/image-processing";
import { detectDragContentType, type SupportedFormatType, SUPPORTED_IMAGE_FORMATS } from "@/lib/supported-formats";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslations } from 'next-intl';
import { useTranslationRTL } from './rtl-context';
import { useRtl } from '@/utils/hooks/useRTL';
import { useImageSettings } from '@/utils/hooks/useImageSettings';
import { usePWADetection } from '@/utils/hooks/usePWADetection';
import { getHtmlLangFromSelectedLanguage } from '@/utils/language-conversion';
import type { useDynamicFontSize } from '@/utils/hooks/useDynamicFontSize';

interface InputBoxProps {
  inputText: string;
  setInputText: (text: string) => void;
  isBusy: boolean;
  lang?: SelectedLanguage;
  onTranslate?: () => void;
  onClear?: (undoClear: boolean) => void;
  context: TranslationContext;
  setContext: (context: TranslationContext) => void;
  onTextExtracted?: (text: string, context?: string) => void;
  dynamicFontSize: ReturnType<typeof useDynamicFontSize>;
}

export function InputBox({
  inputText,
  setInputText,
  isBusy,
  lang,
  onTranslate,
  onClear,
  context,
  setContext,
  onTextExtracted,
  dynamicFontSize
}: InputBoxProps) {
  const [lastClearedText, setLastClearedText] = useState('');
  const [lastClearedContext, setLastClearedContext] = useState<TranslationContext>({});
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragContentType, setDragContentType] = useState<SupportedFormatType | 'unsupported' | null>(null);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const inputTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fontSizeDebounceRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const t = useTranslations('inputBox');
  const { inputDirection, inputTextAlign } = useTranslationRTL();
  const { direction } = useRtl();
  const { maxImageSize } = useImageSettings();
  const { isMobile } = usePWADetection();

  // Combine external isBusy with internal processing states
  const isLocalBusy = isBusy || isProcessingImage;

  useEffect(() => {
    return () => {
      if (fontSizeDebounceRef.current) {
        clearTimeout(fontSizeDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = dynamicFontSize.createResizeObserver(
      dynamicFontSize.updateInputDimensions
    );

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [dynamicFontSize]);

  useEffect(() => {
    if (inputText && lastClearedText) {
      setLastClearedText('');
    }
  }, [inputText, lastClearedText]);

  useEffect(() => {
    if (hasAnyValue(context) && hasAnyValue(lastClearedContext)) {
      setLastClearedContext({});
    }
  }, [context, lastClearedContext]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setInputText(newText);

    // Update ref immediately (no calculation)
    dynamicFontSize.updateInputText(newText);

    // Mark as typing to block ResizeObserver calculations
    dynamicFontSize.setTyping(true);

    // Debounce the calculation only
    if (fontSizeDebounceRef.current) {
      clearTimeout(fontSizeDebounceRef.current);
    }
    fontSizeDebounceRef.current = setTimeout(() => {
      dynamicFontSize.setTyping(false);
      dynamicFontSize.calculate();
    }, 150);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      if (onTranslate) {
        onTranslate();
      }
    }
  };

  const handleContextChange = (newContext: TranslationContext) => {
    if (setContext) {
      setContext(newContext);
    }
  };

  // Helper function to render drag overlay content based on detected content type
  const renderDragOverlayContent = () => {
    switch (dragContentType) {
      case 'images':
        return (
          <>
            <FileImage className="h-8 w-8 text-accent-foreground/90 mb-2" />
            <div className="text-accent-foreground/90 text-sm font-medium">
              {t('dropImageHere')}
            </div>
          </>
        );
      case 'documents':
        return (
          <>
            <FileText className="h-8 w-8 text-amber-500 mb-2" />
            <div className="text-amber-500 text-sm font-medium">
              {t('documentsComingSoon')}
            </div>
          </>
        );
      case 'unsupported':
      default:
        return (
          <>
            <FileQuestion className="h-8 w-8 text-muted-foreground mb-2" />
            <div className="text-muted-foreground text-sm font-medium text-center">
              {t('unsupportedFileType')}
            </div>
          </>
        );
    }
  };

  const getDragOverlayBorderColor = () => {
    switch (dragContentType) {
      case 'images':
        return '!border-blue-500';
      case 'documents':
        return '!border-amber-500';
      case 'unsupported':
      default:
        return '!border-red-500';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if dragged items contain files
    const hasFiles = Array.from(e.dataTransfer.types).includes('Files');
    if (hasFiles && !isDragOver) {
      setIsDragOver(true);

      // Detect content type from MIME types
      const items = Array.from(e.dataTransfer.items);
      const mimeTypes = items.map(item => item.type).filter(Boolean);
      const contentType = detectDragContentType(mimeTypes);
      setDragContentType(contentType);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only set drag over to false if we're leaving the container entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragOver(false);
      setDragContentType(null);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setDragContentType(null);

    if (isLocalBusy || !onTextExtracted) return;

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => SUPPORTED_IMAGE_FORMATS.includes(file.type as any));

    if (imageFiles.length > 0) {
      await processImageFile({
        file: imageFiles[0],
        sourceLang: lang?.name,
        maxImageSize,
        onTextExtracted,
        onProcessingChange: setIsProcessingImage
      });
    }
  };

  return (
    <div
      ref={containerRef}
      className={`flex flex-col gap-1 ${styles.translationBox} ${inputDirection} 
      ${isDragOver ? `border-dashed ${getDragOverlayBorderColor()}` : ''} 
      transition-all duration-200 relative`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <>
          {/* Translucent background overlay */}
          <div className="absolute inset-1 rounded-md z-10 pointer-events-none bg-background/10 backdrop-blur-sm" />

          {/* Content layer with border, icon and text */}
          <div className={`absolute inset-1 rounded-md z-20 pointer-events-none items-center justify-center flex flex-col`}>
            {renderDragOverlayContent()}
          </div>
        </>
      )}

      {isProcessingImage && (
        <div className="absolute inset-0 bg-background/50 backdrop-blur-[2px] flex items-center justify-center rounded-md z-20">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      <textarea
        ref={inputTextAreaRef}
        className={`${styles.translationTextArea} ${inputTextAlign} flex-1`}
        style={{ fontSize: `${dynamicFontSize.fontSize}px` }}
        value={inputText}
        onChange={handleInputChange}
        onKeyDown={handleInputKeyDown}
        spellCheck={true}
        autoCorrect="on"
        inputMode="text"
        enterKeyHint="enter"
        dir={inputDirection}
        lang={getHtmlLangFromSelectedLanguage(lang)}
      />

      <div className="flex gap-2 md:py-1" dir={direction}>
        <TooltipProvider>
          {!isMobile && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={async () => {
                    try {
                      const text = await pasteFromClipboard();

                      if (text) {
                        const newText = inputText + (inputText ? ' ' : '') + text;
                        // Pre-calculate font size before setting text to prevent jumpiness
                        dynamicFontSize.setTextWithOptimalFont(newText, undefined);
                        setInputText(newText);
                        setTimeout(() => {
                          const len = newText.length;
                          inputTextAreaRef.current?.focus();
                          inputTextAreaRef.current?.setSelectionRange(len, len);
                        }, 0);
                      }
                    } catch (err) {
                      console.error('Failed to read clipboard: ', err);
                    }
                  }}
                  disabled={isLocalBusy}>
                  <Clipboard />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('pasteButton')}</p>
              </TooltipContent>
            </Tooltip>
          )}

          <TranscribeButton
            onTranscriptionComplete={(text) => {
              const newText = inputText + (inputText ? ' ' : '') + text;
              // Pre-calculate font size before setting text to prevent jumpiness
              dynamicFontSize.setTextWithOptimalFont(newText, undefined);
              setInputText(newText);
            }}
            disabled={isLocalBusy}
            language={lang?.name}
            prevContent={inputText}
          />

          <ImageToTextButton
            onTextExtracted={(text, context) => {
              if (onTextExtracted) {
                onTextExtracted(text, context);
              }
            }}
            disabled={isLocalBusy}
            sourceLang={lang?.name}
            maxImageSize={maxImageSize}
            onProcessingChange={setIsProcessingImage}
          />

          <div className="transition-all duration-200 ease-in-out">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    if (lastClearedText) {
                      const newText = lastClearedText;
                      // Pre-calculate font size before setting text
                      dynamicFontSize.setTextWithOptimalFont(newText, undefined);
                      setInputText(newText);
                      setLastClearedText('');
                      setContext(lastClearedContext);
                      setLastClearedContext({});

                      if (onClear)
                        onClear(true);
                    } else {
                      setLastClearedText(inputText);
                      // Pre-calculate font size for empty text
                      dynamicFontSize.setTextWithOptimalFont('', undefined);
                      setInputText('');
                      setLastClearedContext(context);
                      setContext({});

                      if (onClear)
                        onClear(false);
                    }

                    setTimeout(() => {
                      const len = lastClearedText?.length ?? 0;
                      inputTextAreaRef.current?.focus();
                      inputTextAreaRef.current?.setSelectionRange(len, len);
                    }, 0);
                  }}
                  disabled={!inputText && !lastClearedText}>
                  {lastClearedText ? <Undo /> : <Eraser />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{lastClearedText ? t('undoClearButton') : t('clearButton')}</p>
              </TooltipContent>
            </Tooltip>
          </div>

          <StarkySenseToggleButton
            isTranslating={isBusy}
          />

          <TranslationSettings
            disabled={isLocalBusy}
            context={context}
            onChange={handleContextChange}
          />

          <CharacterCounter
            characterCount={inputText.length}
            disabled={isLocalBusy}
            className="ms-auto flex items-center"
          />

        </TooltipProvider>
      </div>
    </div>
  );
}